import { Step } from "@homebound/beam";
import { ObjectConfig, required } from "@homebound/form-state";
import { CostType, Maybe } from "src/generated/graphqlTypes";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";

export enum PlanBasedBidPackageStep {
  DEFINE_PACKAGE = "Define Package",
  CONFIGURE_BID_GROUPS = "Configure Bid Groups",
  REVIEW_AND_DOWNLOAD = "Review and Download",
}

export type PlanBasedBidPackageRequest = {
  id?: Maybe<string>;
  tradePartnerId?: Maybe<string>;
  archive?: Maybe<boolean>;
};

export type PlanBasedBidPackageBidPlanGroupDimension = {
  id?: Maybe<string>;
  globalOptionTypeId?: Maybe<string>;
  globalOptionIds?: Maybe<string[]>;
};

export type PlanBasedBidPackageBidPlanGroup = {
  id?: Maybe<string>;
  globalOptionId?: Maybe<string>;
  order?: Maybe<number>;
  dimensions?: Maybe<PlanBasedBidPackageBidPlanGroupDimension[]>;
};

export type PlanBasedBidPackageFormState = {
  bidPackageGroupId?: Maybe<string>;
  bidPackageId?: Maybe<string>;
  marketId?: Maybe<string>;
  projects?: Maybe<string[]>;
  costCodes?: Maybe<string[]>;
  costType?: Maybe<CostType>;
  productOfferings?: Maybe<string[]>;
  requests?: Maybe<PlanBasedBidPackageRequest[]>;
  bidPlanGroups?: Maybe<PlanBasedBidPackageBidPlanGroup[]>;
};

export const planBasedBidPackageFormConfig: ObjectConfig<PlanBasedBidPackageFormState> = {
  bidPackageGroupId: { type: "value" },
  bidPackageId: { type: "value" },
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  costCodes: { type: "value", rules: [required] },
  costType: { type: "value", rules: [required] },
  productOfferings: { type: "value" },
  requests: {
    type: "list",
    config: { id: { type: "value" }, tradePartnerId: { type: "value" }, archive: { type: "value" } },
  },
  bidPlanGroups: {
    type: "list",
    config: {
      id: { type: "value" },
      globalOptionId: { type: "value" },
      order: { type: "value" },
      dimensions: {
        type: "list",
        config: { id: { type: "value" }, globalOptionTypeId: { type: "value" }, globalOptionIds: { type: "value" } },
      },
    },
  },
};

const INCOMPLETE = "incomplete" as const;

export function initialSteps(isCreateMode: boolean = true): Step[] {
  return Object.values(PlanBasedBidPackageStep).map((step) => ({
    value: step,
    label: step,
    state: INCOMPLETE,
    disabled: isCreateMode,
  }));
}

/** Builds a dimensions list from the checked type ids. Keeps existing rows as-is and seeds new ones with every value selected. */
export function buildDimensions(
  selectedTypeIds: string[],
  existing: PlanBasedBidPackageBidPlanGroupDimension[],
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"],
): PlanBasedBidPackageBidPlanGroupDimension[] {
  return selectedTypeIds.map((typeId) => {
    const prior = existing.find((d) => d.globalOptionTypeId === typeId);
    if (prior) return prior;
    const result = dimensionResults.find((r) => r.type.id === typeId);
    return { globalOptionTypeId: typeId, globalOptionIds: result?.options.map((go) => go.id) ?? [] };
  });
}
